import { useState, useMemo } from 'react';
import { products } from '@/mocks/products';
import { layers } from '@/mocks/layers';
import Header from './components/Header';
import MobileMenu from './components/MobileMenu';
import NeuralHero from './components/NeuralHero';
import LayerSection from './components/LayerSection';
import ArchitectureGraph from './components/ArchitectureGraph';
import EcologySection from './components/EcologySection';
import ProductModal from './components/ProductModal';
import ProductCard from './components/ProductCard';

const categoryConfig: Record<string, { name: string; color: string; icon: string }> = {
  strategic: { name: '战略引擎', color: '#FF6B35', icon: 'ri-rocket-2-line' },
  foundation: { name: '基础能力', color: '#00D4FF', icon: 'ri-cpu-line' },
  marketing: { name: '品牌营销', color: '#A855F7', icon: 'ri-megaphone-line' },
  fishery: { name: '渔业领域', color: '#10B981', icon: 'ri-water-flash-line' },
};

export default function HomePage() {
  const [keyword, setKeyword] = useState('');
  const [active, setActive] = useState<typeof products[0] | null>(null);
  const [menu, setMenu] = useState(false);
  const [activeTab, setActiveTab] = useState<'matrix' | 'architecture'>('matrix');

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return [];
    return products.filter((p) =>
      [p.name, p.slogan, p.value, p.layer, ...(p.tags || [])]
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [keyword]);

  return (
    <main className="min-h-screen bg-[#030712] text-white selection:bg-[#00D4FF]/25 relative">
        {/* 背景网格 */}
      <div className="fixed inset-0 z-0 opacity-15 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(0,212,255,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0,212,255,0.1) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      {/* 浮动粒子装饰 */}
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
        {Array.from({ length: 15 }).map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-[#00D4FF] rounded-full opacity-30"
            style={{
              left: `${(i * 7) % 100}%`,
              top: `${(i * 13) % 100}%`,
              animation: `float ${8 + (i % 7)}s ease-in-out infinite`,
              animationDelay: `${i * 0.3}s`,
            }}
          />
        ))}
      </div>

      {/* 顶部导航 */}
      <Header keyword={keyword} setKeyword={setKeyword} menu={menu} setMenu={setMenu} />

      {/* 移动端菜单 */}
      <MobileMenu open={menu} keyword={keyword} setKeyword={setKeyword} onClose={() => setMenu(false)} />

      {/* 主内容 */}
      <div className="relative z-10">
        {/* Hero */}
        <NeuralHero />

        {/* 视图切换 */}
        <section className="px-6 md:px-8 mb-8 mt-4 md:mt-8">
          <div className="max-w-7xl mx-auto flex justify-center">
            <div className="inline-flex p-1 bg-white/5 border border-white/10 rounded-full">
              <button
                onClick={() => setActiveTab('matrix')}
                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  activeTab === 'matrix'
                    ? 'bg-[#00D4FF] text-[#030712]'
                    : 'text-white/60 hover:text-white'
                }`}
              >
                <i className="ri-grid-fill mr-2"></i>
                产品矩阵
              </button>
              <button
                onClick={() => setActiveTab('architecture')}
                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  activeTab === 'architecture'
                    ? 'bg-[#00D4FF] text-[#030712]'
                    : 'text-white/60 hover:text-white'
                }`}
              >
                <i className="ri-mind-map mr-2"></i>
                架构关系图
              </button>
            </div>
          </div>
        </section>

        {/* 搜索结果或分层展示 */}
        {keyword.trim() ? (
          <section className="px-6 md:px-12 py-16 md:py-20 max-w-7xl mx-auto">
            <div className="mb-10">
              <div className="text-sm tracking-[0.35em] text-[#00D4FF] mb-4">SEARCH</div>
              <h2 className="text-3xl md:text-5xl font-bold mb-4 text-white">搜索结果</h2>
              <p className="text-white/60">共找到 {filtered.length} 个相关产品</p>
            </div>
            {filtered.length ? (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {filtered.map((item, idx) => (
                  <ProductCard key={item.id} product={item} index={idx} onOpen={setActive} />
                ))}
              </div>
            ) : (
              <div className="rounded-3xl bg-white/5 border border-white/10 p-10 text-white/60 text-center">
                没有找到匹配产品，请尝试搜索&ldquo;渔&rdquo;&ldquo;AI&rdquo;&ldquo;战略&rdquo;&ldquo;客服&rdquo;等关键词。
              </div>
            )}
          </section>
        ) : activeTab === 'matrix' ? (
          <>
            {/* 分类筛选 */}
            <section className="px-6 md:px-12 mb-8 max-w-7xl mx-auto">
              <div className="flex flex-wrap items-center justify-center gap-3">
                {Object.entries(categoryConfig).map(([key, config]) => (
                  <a
                    key={key}
                    href={`#${key}`}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-white/10 bg-white/5 text-white/60 hover:text-white hover:border-white/20 transition-all duration-300 text-sm cursor-pointer whitespace-nowrap"
                  >
                    <i className={`${config.icon} text-lg`} style={{ color: config.color }}></i>
                    <span className="font-medium">{config.name}</span>
                    <span className="text-xs opacity-50">
                      ({products.filter((p) => p.category === key).length})
                    </span>
                  </a>
                ))}
              </div>
            </section>

            {/* 产品分层 */}
            {layers.map((layer) => (
              <LayerSection key={layer.id} layer={layer} onOpen={setActive} />
            ))}
          </>
        ) : (
          /* 架构关系图 */
          <section className="px-6 md:px-12 py-16 md:py-20 max-w-7xl mx-auto">
            <div className="p-6 bg-white/5 border border-white/10 rounded-2xl">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-xl font-bold text-white mb-1">产品协同架构</h2>
                  <p className="text-sm text-white/40">悬停节点查看产品详情，连线展示数据流转与协同关系</p>
                </div>
                <div className="flex flex-wrap items-center gap-4">
                  {Object.entries(categoryConfig).map(([key, config]) => (
                    <div key={key} className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: config.color }}></span>
                      <span className="text-xs text-white/60">{config.name}</span>
                    </div>
                  ))}
                </div>
              </div>
              <ArchitectureGraph />
            </div>
          </section>
        )}

        {/* 生态协同说明 */}
        <EcologySection />

        {/* Footer */}
        <footer className="px-6 md:px-12 py-16 md:py-24 text-center border-t border-white/10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 max-w-7xl mx-auto mb-8">
            <div className="flex items-center gap-4">
              <img
                src="https://static.readdy.ai/image/9e747d83165d349a03f5427ea4fab807/ff89e372032fff0d5aff6b38fd727991.png"
                alt="陆渔科技"
                className="h-8 w-auto opacity-60"
              />
              <div className="text-sm text-white/40">陆渔科技 · 确定性农业开创者</div>
            </div>
            <div className="flex items-center gap-6">
              <a
                href="https://luyuaqua.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-white/40 hover:text-[#00D4FF] transition-colors cursor-pointer"
              >
                官网
              </a>
              <a
                href="https://auaa.luyuaqua.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-white/40 hover:text-[#00D4FF] transition-colors cursor-pointer"
              >
                确定性农业引擎
              </a>
            </div>
          </div>
          <p className="text-xs text-white/30">
            23 Products Unified · Production-Ready React Prototype · Luyu Aqua Deterministic Agriculture OS
          </p>
        </footer>
      </div>

      {/* 产品详情弹窗 */}
      <ProductModal product={active} onClose={() => setActive(null)} />

      {/* 全局 CSS */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0) translateX(0);
          }
          25% {
            transform: translateY(-20px) translateX(10px);
          }
          50% {
            transform: translateY(-10px) translateX(-10px);
          }
          75% {
            transform: translateY(-30px) translateX(5px);
          }
        }

        @keyframes gradient {
          0% {
            background-position: 0% center;
          }
          50% {
            background-position: 100% center;
          }
          100% {
            background-position: 0% center;
          }
        }

        .animate-gradient {
          animation: gradient 4s ease infinite;
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }

        .animate-scaleIn {
          animation: scaleIn 0.3s ease-out;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        ::-webkit-scrollbar {
          width: 6px;
        }
        ::-webkit-scrollbar-track {
          background: transparent;
        }
        ::-webkit-scrollbar-thumb {
          background: rgba(0, 212, 255, 0.3);
          border-radius: 3px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 212, 255, 0.5);
        }
      `}</style>
    </main>
  );
}